/**
 * 语音输入区域组件
 * 提供语音录制和上传功能
 *
 * @component VoiceInputSection
 * @props {Object} scores - 评分数据对象
 * @emits voice-input - 语音输入完成事件，参数为转写的文本内容
 */

<template>
  <div class="voice-input-section">
    <div class="voice-controls" v-if="showControls">
      <button
        class="voice-btn"
        @click="toggleRecording"
        :class="{ recording: isRecording }"
      >
        <el-icon><svg-icon name="vector" /></el-icon>
        {{ voiceWebsocketConnecting ? '连接中...' : (isRecording ? '停止录音' : '录制语音') }}
      </button>
      <div class="voice-upload">
        <button
          class="voice-upload-btn"
          @click="triggerVoiceFileInput"
        >
          <el-icon><svg-icon name="folder" /></el-icon>
          上传
        </button>
        <input
          type="file"
          ref="voiceFileInput"
          accept="audio/*"
          @change="handleVoiceFileChange"
          style="display: none"
        />
      </div>
    </div>
    <div class="voice-content" ref="scrollRef">

      <!-- <div class="voice-status" v-if="isRecording || recordingTime > 0">
        <span v-if="recordingTime >= 0" class="recording-time">{{ formatTime(recordingTime) }}</span>
        <el-icon :size="24" :class="{ 'voice-status-recording': isRecording }"><svg-icon name="voice" /></el-icon>
        <el-icon :size="20" class="voice-close" @click="clearRecording"><svg-icon v-if="!isRecording" name="close" /></el-icon>
      </div> -->

      <div class="voice-file-list" v-if="voiceFiles.length > 0">
        <div v-for="(file, index) in voiceFiles" :key="index" :id="file.uid" class="file-item" v-loading="file.loading">
          <template v-if="file.recording">
            <div class="recording-info">
              <el-icon :size="20" :class="{'expanded-icon': expandKeys.includes(file.uid)}" @click="handleChange(file.uid)"><svg-icon name="menu-fold" /></el-icon>
              <span class="file-name">{{ file.name }}</span>
              <el-icon :size="20" class="file-recording" v-if="!file.progress"><svg-icon name="voice" /></el-icon>
              <el-button link type="info" size="small" v-else-if="showControls" @click="handleRemoveFile(index)">删除</el-button>
            </div>
            <div class="file-actions">
              <div class="play-action" v-if="file.file">
                <el-icon :size="20" @click="handlePause(file, index)" v-if="file.play"><svg-icon name="pause" /></el-icon>
                <el-icon :size="20" @click="handlePlay(file, index)" v-else><svg-icon name="play" /></el-icon>
                <span v-if="file.duration">{{ formatDuration(file.duration) }}</span>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="file-info">
              <el-icon :size="20" :class="{'expanded-icon': expandKeys.includes(file.uid)}" @click="handleChange(file.uid)"><svg-icon name="menu-fold" /></el-icon>
              <span class="file-name">{{ file.name }}</span>
            </div>
            <div class="file-actions">
              <div class="play-action">
                <el-icon :size="20" @click="handlePause(file, index)" v-if="file.play"><svg-icon name="pause" /></el-icon>
                <el-icon :size="20" @click="handlePlay(file, index)" v-else><svg-icon name="play" /></el-icon>
                <span v-if="file.duration">{{ formatDuration(file.duration) }}</span>
              </div>
              <template v-if="showControls">
                <el-button
                  type="primary"
                  link
                  size="small"
                  v-if="file.progress === 0"
                  :disabled="isVoiceExtracting"
                  @click="startVoiceExtract(file)"
                >
                  开始提取
                </el-button>
                <el-progress
                  v-else
                  :percentage="file.progress"
                  :status="file.progress === 100 ? 'success' : ''"
                />
                <el-button link type="info" size="small" @click="handleRemoveFile(index)">
                  删除
                </el-button>
              </template>
            </div>
          </template>
          <div class="file-result" v-if="file.text && expandKeys.includes(file.uid)">
            <textarea :rows="1" :data-field="file.uid" v-model="file.text" @input="adjustTextareaHeight(file)" />
            <div class="result-actions" v-if="showControls && file.progress === 100">
              <el-button type="primary" size="small" plain v-if="file.file" @click="handleDownload(file)">
                下载录音
              </el-button>
              <el-button type="primary" size="small" plain @click="confirmVoiceInput(file)" :loading="loadingObj[file.uid]">
                确认使用
              </el-button>
            </div>
          </div>
        </div>
      </div>
      <audio id="audio-player" style="display: none;"></audio>
    </div>
    <div class="voice-backtop" @click="handleBackTop">
      <el-icon ><svg-icon name="writing/backtop" /></el-icon>
      返回顶部
    </div>
  </div>
</template>

<script setup>
import { ref, watch, onUnmounted, nextTick, unref, onMounted } from 'vue'
import { Microphone, Upload, Document, Clock } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import WebAudioSpeechRecognizer from '@/utils/asr/webaudiospeechrecognizer'
import { WebSocketConnectMethod, WebSocketStopMethod } from '@/utils/wsconnecter'
import { fetchUpload, fetchDeleteFile } from '@/api/project'
import { fetchSaveUploadValue } from '@/api/writing'
import {audioPermission} from "@/utils/permission";

const props = defineProps({
  showControls: {
    type: Boolean,
    default: true
  },
  projectId: {
    type: String,
    default: ''
  },
  defaultValue: {
    type: Array,
    default: () => ([])
  }
})

const emit = defineEmits(['voice-input', 'file-change'])

const scrollRef = ref()
const isRecording = ref(false)
const recordingTime = ref(0)
const voiceText = ref('')
const voiceFiles = ref(props.defaultValue || [])
const isVoiceExtracting = ref(false)
const voiceFileInput = ref(null)
let recordingTimer = null
let recognizer = null
const currentSentence = ref('')
const extractedSourceFile = ref('')
const audioRecordingData = ref()
// 当前录音的索引
const recordingIndex = ref()
// 展开的数据
const expandKeys = ref([])
const loadingObj = ref({})
const voiceOfflineRecorder = ref(null)
const voiceSampleBuf = ref(new Int16Array())
const voiceWebsocketConnecting = ref(false)
const voiceWebsocket = WebSocketConnectMethod({ stateHandle }, process.env.VUE_APP_VIOCE_REALTIME)
const fileWebsocket = WebSocketConnectMethod({ stateHandle, isfilemode: true }, process.env.VUE_APP_VIOCE_FILE)
const isOnline = process.env.VUE_APP_VOICE_ONLINE === 'true'

function stateHandle(connState) {
  if ( connState === 0 ) {
    // 连接成功
    console.log('stateHandle: 0 连接成功')
  } else if ( connState === 1 ) {
		// 连接断开
    console.log('stateHandle: 1 连接断开')
    isVoiceExtracting.value = false
    voiceWebsocketConnecting.value = false
    voiceOfflineRecorder.value = null
    voiceSampleBuf.value = new Int16Array()
	} else if ( connState === 2 ) {
    // 连接失败
    isVoiceExtracting.value = false
    voiceWebsocketConnecting.value = false
    voiceOfflineRecorder.value = null
    voiceSampleBuf.value = new Int16Array()
    ElMessage.error('语音服务连接失败')
  }
}

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60)
  const secs = seconds % 60
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
}

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const toggleRecording = () => {
  audioPermission().then(state => {
    if(state=="denied"){
      ElMessage.error('录音权限被拒绝，请在浏览器设置中允许麦克风访问')
    }else {
      if (isRecording.value) {
        stopRecording()
      } else {
        startRecording()
      }
    }
  });
}

// 在线录音
const startRecordingOnline = async ({ onStart }) => {
  try {
    const params = {
      secretid: process.env.VUE_APP_TENCENT_SECRET_ID,
      secretkey: process.env.VUE_APP_TENCENT_SECRET_KEY,
      appid: Number(process.env.VUE_APP_TENCENT_APPID),
      engine_model_type: '16k_zh',
      voice_format: 1,
      needvad: 1,
      filter_dirty: 0,
      filter_modal: 0,
      filter_punc: 0,
      convert_num_mode: 1,
      word_info: 0
    }

    recognizer = new WebAudioSpeechRecognizer(params, true)

    recognizer.OnRecognitionStart = () => {
      onStart();
    }

    recognizer.OnSentenceBegin = (res) => {
      if (res.result?.voice_text_str) {
        currentSentence.value = res.result.voice_text_str
        voiceFiles.value[recordingIndex.value].text += currentSentence.value
        adjustTextareaHeight(voiceFiles.value[recordingIndex.value])
      }
    }

    recognizer.OnReceivedData = (_, res) => {
      audioRecordingData.value = res
    }

    recognizer.OnRecognitionResultChange = (res) => {
      if (res.result?.voice_text_str) {
        if (currentSentence.value) {
          voiceFiles.value[recordingIndex.value].text = voiceFiles.value[recordingIndex.value].text.slice(0, -currentSentence.value.length)
        }
        currentSentence.value = res.result.voice_text_str
        voiceFiles.value[recordingIndex.value].text += currentSentence.value
        adjustTextareaHeight(voiceFiles.value[recordingIndex.value])
      }
    }

    recognizer.OnSentenceEnd = (res) => {
      if (res.result?.voice_text_str) {
        if (currentSentence.value) {
          voiceFiles.value[recordingIndex.value].text = voiceFiles.value[recordingIndex.value].text.slice(0, -currentSentence.value.length)
        }
        currentSentence.value = res.result.voice_text_str
        voiceFiles.value[recordingIndex.value].text += currentSentence.value
        currentSentence.value = ''
        adjustTextareaHeight(voiceFiles.value[recordingIndex.value])
      }
    }

    recognizer.OnRecognitionComplete = () => {
      stopRecording()
    }

    recognizer.OnError = (error) => {
      console.error('识别错误:', error)
      ElMessage.error('识别错误：' + (error.message || '未知错误'))
      stopRecording()
    }

    recognizer.start()

  } catch (error) {
    console.error('录音初始化失败:', error)
    ElMessage.error('启动录音失败：' + error.message)
    stopRecording()
  }
}

// 离线录音
const startRecordingOffline = async ({ onStart }) => {
  voiceWebsocketConnecting.value = true
  await voiceWebsocket.wsStart({
    onOpen: () => {
      voiceWebsocketConnecting.value = false

      voiceOfflineRecorder.value = Recorder({
        type:"pcm",
        bitRate:16,
        sampleRate:16000,
        onProcess: (buffer, powerLevel, bufferDuration, bufferSampleRate,newBufferIdx,asyncEnd) => {
          const data_48k = buffer[buffer.length-1];
          const  array_48k = new Array(data_48k);
          const data_16k=Recorder.SampleData(array_48k,bufferSampleRate,16000).data;

          voiceSampleBuf.value = Int16Array.from([...voiceSampleBuf.value, ...data_16k]);
          const chunk_size=960; // for asr chunk_size [5, 10, 5]

          while(voiceSampleBuf.value.length>=chunk_size){
            const sendBuf=voiceSampleBuf.value.slice(0,chunk_size);
            voiceSampleBuf.value=voiceSampleBuf.value.slice(chunk_size,voiceSampleBuf.value.length);
            voiceWebsocket.wsSend(sendBuf);
          }
        },
      });

      voiceOfflineRecorder.value.open( function(){
        voiceOfflineRecorder.value.start();
        onStart();
      });
    },
    onMessage: (jsonMsg) => {
      const contentText = JSON.parse(jsonMsg.data)['text'];
      voiceFiles.value[recordingIndex.value].text += contentText
      adjustTextareaHeight(voiceFiles.value[recordingIndex.value])
    },
  })
}

const startRecording = async () => {
  // 获取当前最大的录音序号
  const maxIndex = voiceFiles.value.reduce((max, file) => {
    if (file.name && file.name.startsWith('录音录入')) {
      const match = file.name.match(/录音录入(\d+)/);
      if (match) {
        const num = parseInt(match[1]);
        return num > max ? num : max;
      }
    }
    return max;
  }, 0);

  const index = maxIndex + 1;
  const fileName = `录音录入${index}.wav`;

  function onConnect() {
    currentSentence.value = ''
    recordingTime.value = 0
    recordingTimer = setInterval(() => {
      recordingTime.value++
    }, 1000)
  }

  function onStart() {
    const uid = `voice-recording-${index}`
    isRecording.value = true
    recordingIndex.value = voiceFiles.value.length
    voiceFiles.value.push({
      uid,
      index,
      recording: true,
      progress: 0,
      name: fileName,
      text: ''
    })
    handleExpand(uid, true)
    ElMessage.success('开始录音')
  }

  if (isOnline) {
    onConnect();
    await startRecordingOnline({ onStart, fileName, index })
  } else {
    startRecordingOffline({ onConnect, onStart})
  }
}

// 离线停止
const stopWebsocketOffline = (wsconnecter, isfilemode) => {
  WebSocketStopMethod(wsconnecter, isfilemode)
}

const stopRecording = () => {
  function onEndRecording(audioBlob, callback) {
    if (audioBlob) {
      const file = new File([audioBlob], voiceFiles.value[recordingIndex.value].name, { type: 'audio/wav' });
      voiceFiles.value[recordingIndex.value].file = file;

      // 如果有projectId，上传文件
      if (props.projectId) {
        const formData = new FormData();
        formData.append('files', file);
        formData.append('fileType', 1);
        formData.append('projectId', props.projectId);

        voiceFiles.value[recordingIndex.value].loading = true;
        fetchUpload(formData).then((res) => {
          if (res) {
            voiceFiles.value[recordingIndex.value].loading = false;
            voiceFiles.value[recordingIndex.value].id = res.id;
            // 获取音频时长
            getAudioDuration(res.fileUrl, (duration) => {
              voiceFiles.value[recordingIndex.value].duration = duration;
            });
          } else {
            ElMessage.warning('文件上传失败');
            voiceFiles.value.splice(recordingIndex.value, 1);
          }
        }).catch(() => {
          ElMessage.warning('文件上传失败');
          voiceFiles.value.splice(recordingIndex.value, 1);
        });
      } else {
        // 如果没有projectId，只获取音频时长
        const url = URL.createObjectURL(file);
        getAudioDuration(url, (duration) => {
          voiceFiles.value[recordingIndex.value].duration = duration;
          URL.revokeObjectURL(url);
        });
      }
    }

    callback?.();

    isRecording.value = false;
    voiceFiles.value[recordingIndex.value].progress = 100;
    clearInterval(recordingTimer);
  }

  if (isOnline) {
    if (recognizer) {
      const audioBlob = recognizer.saveAudioData();
      onEndRecording(audioBlob, () => {
        recognizer.stop();
        recognizer = null;
      });
    } else {
      isRecording.value = false;
      voiceFiles.value[recordingIndex.value].progress = 100;
      clearInterval(recordingTimer);
    }
  } else {
    if(voiceSampleBuf.value.length>0){
      voiceWebsocket.wsSend(voiceSampleBuf.value);
      voiceSampleBuf.value=new Int16Array();
		}

    isRecording.value = false;
    clearInterval(recordingTimer);

    stopWebsocketOffline(voiceWebsocket, false)

    voiceOfflineRecorder.value.stop(function(blob) {
      const audioBlob = Recorder.pcm2wav({sampleRate:16000, bitRate:16, blob:blob},
        function(theblob,duration){
          onEndRecording(theblob);
        },function(msg){
          console.log('voiceOfflineRecorder stop error: ' + msg);
        }
      )
    })
  }
}

const clearRecording = () => {
  recordingTime.value = 0;
  clearVoiceInput();
}

const triggerVoiceFileInput = () => {
  voiceFileInput.value.click()
}

const handleVoiceFileChange = (event) => {
  const files = Array.from(event.target.files)
  const uids = []

  // 验证文件类型和大小
  const validFiles = files.filter(file => {
    const isValidSize = file.size <= 1024 * 1024 *1024 // 1G 限制

    if (!isValidSize) {
      ElMessage.warning(`文件 ${file.name} 超过1G限制`)
    }

    return isValidSize
  })

  if (!validFiles.length) return

  validFiles.forEach((file, index) => {
    const uid = new Date().getTime() + '_' + Math.random().toString(36).substr(2, 9) + '_' + (index + 1)
    uids.push(uid)
    voiceFiles.value.push({
      uid,
      name: file.name,
      size: file.size,
      progress: 0,
      loading: true,
      file
    })
  })

  if (props.projectId) {
    const formData = new FormData()
    files.forEach(file => {
      formData.append('files', file)
    })
    formData.append('fileType', 1)
    formData.append('projectId', props.projectId)
    fetchUpload(formData).then((res) => {
      if (res) {
        uids.forEach((uid) => {
          const fileIndex = voiceFiles.value.findIndex(v => v.uid === uid)
          voiceFiles.value[fileIndex].loading = false
          voiceFiles.value[fileIndex].id = res.id
          voiceFiles.value[fileIndex].url = res.fileUrl
          getAudioDuration(res.fileUrl, (duration) => {
            voiceFiles.value[fileIndex].duration = duration
          })
        })
      } else {
        uids.forEach((uid) => {
          const fileIndex = voiceFiles.value.findIndex(v => v.uid === uid)
          voiceFiles.value.splice(fileIndex, 1)
        })
        ElMessage.warning(`文件上传失败`)
      }
    }).catch(() => {
      uids.forEach((uid) => {
        const fileIndex = voiceFiles.value.findIndex(v => v.uid === uid)
        voiceFiles.value.splice(fileIndex, 1)
      })
      ElMessage.warning(`文件上传失败`)
    })
  }

  event.target.value = ''
}

const clearVoiceFiles = () => {
  voiceFiles.value = []
}

const endVoiceExtract = async (result, file, progressTimer) => {
  // 清除进度条计时器
  if (progressTimer) {
    clearInterval(progressTimer)
  }
  // 设置100%进度
  const index = voiceFiles.value.findIndex(v => v.uid === file.uid)

  if (result.code === 0) {
    voiceFiles.value[index].progress = 100
    voiceFiles.value[index].text = result.content.text
    extractedSourceFile.value = result.content.filename
    adjustTextareaHeight(voiceFiles.value[index])
    nextTick(() => {
      setTimeout(() => {
        handleScrollBottom()
      }, 1000);
    })

    await fetchSaveUploadValue({
      id: file.id,
      value: result.content.text
    })

    ElMessage.success('语音转写完成')
  } else {
    voiceFiles.value[index].progress = 0
    console.error(result.message || '转写失败')
  }
}

const startVoiceExtract = async (file) => {
  if (isVoiceExtracting.value) return
  isVoiceExtracting.value = true

  // 创建进度条计时器
  let progressTimer = null
  const simulateProgress = () => {
    const index = voiceFiles.value.findIndex(v => v.uid === file.uid)
    progressTimer = setInterval(() => {
      voiceFiles.value[index].progress = Math.min(Math.floor(file.progress + Math.random() * 3 + 1), 99)
    }, 200)
  }

  handleExpand(file.uid, true)

  try {
    // 开始模拟进度
    simulateProgress()
    const index = voiceFiles.value.findIndex(v => v.uid === file.uid)

    if (isOnline) {
      // 在线转写
      const response = await fetch('/api/voice/recognize', {
        method: 'POST',
        body: JSON.stringify({ url: file.url })
      })

      const result = await response.json()

      endVoiceExtract(result, file, progressTimer)
    } else {
      let contentText = '';
      // 离线转写
      await fileWebsocket.wsStart({
        file_ext: file.name.split('.').pop().toLowerCase(),
        onMessage: (jsonMsg) => {
          const rectxt=""+JSON.parse(jsonMsg.data)['text'];
          const asrmodel=JSON.parse(jsonMsg.data)['mode'];
          const is_final=JSON.parse(jsonMsg.data)['is_final'];
          // const timestamp=JSON.parse(jsonMsg.data)['timestamp'];
          console.log('msgHandle: rectxt' + rectxt);

          contentText = contentText + rectxt;
          endVoiceExtract({ code: 0, content: { text: contentText, filename: file.name } }, file, progressTimer)
          if (is_final==true) {
            fileWebsocket.wsStop();
          }
        },
        onError: () => {
          isVoiceExtracting.value = false
          endVoiceExtract({ code: 1, message: '转写失败' }, file, progressTimer)
        }
      });

      const fileAudio = new FileReader();
      fileAudio.readAsArrayBuffer(file.file);
      fileAudio.onload = () => {
        const file_data_array = fileAudio.result;
        let sampleBuf=new Uint8Array( file_data_array );
        let chunk_size=960; // for asr chunk_size [5, 10, 5]
        let totalsend=0;
        while(sampleBuf.length>=chunk_size){
          const sendBuf=sampleBuf.slice(0,chunk_size);
          totalsend=totalsend+sampleBuf.length;
          sampleBuf=sampleBuf.slice(chunk_size,sampleBuf.length);
          fileWebsocket.wsSend(sendBuf);
        }
        if(sampleBuf.length>0){
          fileWebsocket.wsSend(sampleBuf);
          sampleBuf=new Int16Array();
        }
        stopWebsocketOffline(fileWebsocket, true)
      }
    }
  } catch (error) {
    endVoiceExtract({ code: 1, message: '转写失败' }, file, progressTimer)

    ElMessage.error('转写失败：' + error.message)
  } finally {
    isVoiceExtracting.value = false
  }
}

const confirmVoiceInput = async (file) => {
  try {
    loadingObj.value[file.uid] = true
    // 调用 extract 接口提取内容
    const extractResponse = await fetch('/api/writing/extract', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        content: file.text
      })
    })

    if (!extractResponse.ok) {
      const errorData = await extractResponse.json()
      throw new Error(errorData.error || '内容提取失败')
    }

    const extractResult = await extractResponse.json()
    const isEmpty = Object.values(extractResult.result || {}).every(v => !v)

    if (isEmpty) {
      ElMessage.warning('未提取到要点')
      return
    }

    // 更新表单数据 - 使用追加模式
    emit('voice-input', {
      title: extractResult.result.title ? (extractResult.result.title) : '',
      inventor: extractResult.result.inventors ? (extractResult.result.inventors) : '',
      field: extractResult.result.field ? (extractResult.result.field) : '',
      project: extractResult.result.project ? (extractResult.result.project) : '',
      background: extractResult.result.background ? (extractResult.result.background) : '',
      problem: extractResult.result.problem ? (extractResult.result.problem) : '',
      technical: extractResult.result.solution ? (extractResult.result.solution) : '',
      effect: extractResult.result.effect ? (extractResult.result.effect) : '',
      implementation: extractResult.result.implementation ? (extractResult.result.implementation) : '',
      description: extractResult.result.drawings ? (extractResult.result.drawings) : '',
      filename: file.name,
      append: true // 添加标记表示这是追加模式
    })
    extractedSourceFile.value = ''

    ElMessage.success('已追加识别内容')
  } catch (error) {
    console.error('内容提取失败:', error)
    ElMessage.error('内容提取失败: ' + error.message)
  } finally {
    loadingObj.value[file.uid] = false
  }
}

const clearVoiceInput = () => {
  // voiceText.value = ''
}

const handleRemoveFile = (index) => {
  const current = voiceFiles.value[index]
  if(current.recording) {
    voiceFiles.value.splice(index, 1)
    ElMessage.success('删除成功')
    return
  }
  fetchDeleteFile([current.id]).then(res => {
    if (res) {
      voiceFiles.value.splice(index, 1)
      ElMessage.success('删除成功')
    } else {
      ElMessage.warning('删除失败')
    }
  })
}

const handlePlay = (file, index) => {
  const audio = document.getElementById('audio-player');
  if (file.file) {
    const url = URL.createObjectURL(file.file);
    audio.src = url;
    audio.play();
    voiceFiles.value[index].play = true;

    // 播放结束时清理
    audio.onended = () => {
      delete voiceFiles.value[index].play;
      URL.revokeObjectURL(url);
    };
  }
}

const handlePause = (file, index) => {
  const audio = document.getElementById('audio-player');
  audio.pause();
  delete voiceFiles.value[index].play;
}

const handleChange = (uid) => {
  const expanded = expandKeys.value.includes(uid)
  if (expanded) {
    expandKeys.value = expandKeys.value.filter(v => v !== uid)
  } else {
    handleExpand(uid)
  }
}

const handleExpand = (uid, reset = false) => {
  if (reset) {
    expandKeys.value = [uid]
  } else if (!expandKeys.value.includes(uid)) {
    expandKeys.value.push(uid)
    adjustTextareaHeight({ uid })
  }
}

const handleBackTop = () => {
  scrollRef.value.scrollTo({ top: 0, behavior: 'smooth' })
}

const handleScrollBottom = () => {
  scrollRef.value.scrollTo({ top: scrollRef.value.scrollHeight, behavior: 'smooth' })
}


const adjustTextareaHeight = (file) => {
  nextTick(() => {
    const textarea = document.querySelector(`textarea[data-field="${file.uid}"]`)
    if (textarea) {
      textarea.style.height = 'auto'
      textarea.style.height = textarea.scrollHeight + 'px'
    }
  })
}

function getAudioDuration(url, callback) {
  var audio = new Audio();
  audio.src = url;

  audio.onloadedmetadata = function() {
    callback(audio.duration);
  };

  audio.onerror = function() {
    callback();
  };
}

function formatDuration(seconds) {
  if (!seconds) return ''
  seconds = Math.floor(seconds);
  var minutes = Math.floor(seconds / 60);
  seconds = seconds % 60;

  if (minutes < 10) {
    minutes = '0' + minutes;
  }
  if (seconds < 10) {
    seconds = '0' + seconds;
  }
  return minutes + ':' + seconds;
}

const handleDownload = (file) => {
  const url = URL.createObjectURL(file.file);
  const a = document.createElement('a');
  a.href = url;
  a.download = file.name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

watch(() => voiceFiles.value, (newVal) => {
  emit('file-change', [...newVal])
  nextTick(() => {
    handleScrollBottom()
  })
}, { deep: true })

watch(() => props.defaultValue, (newVal) => {
  if (newVal && !voiceFiles.value?.length) {
    voiceFiles.value = [...newVal]
  } else if (!newVal?.length && voiceFiles.value?.length) {
    voiceFiles.value = []
  }
})

onUnmounted(() => {
  if (recognizer) {
    recognizer.destroyStream()
    recognizer = null
  }
  if (recordingTimer) {
    clearInterval(recordingTimer)
  }
})

defineExpose({ expand: handleExpand, getAudioDuration, voiceFiles: unref(voiceFiles) })
</script>

<style scoped lang="scss">
.voice-input-section {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.voice-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 10px 0;
  padding: 0 20px;
  overflow: auto;
}

.voice-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 20px;
}

.voice-btn, .voice-upload-btn {
  width: 62%;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  color: var(--el-color-primary);
  font-weight: 600;
  background: rgba(0,161,96,0.06);
  border: 1px solid var(--el-color-primary);
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  .el-icon {
    font-size: 18px;
  }
  &:hover {
    background: rgb(229.5, 245.6, 239.1);
  }
}

.voice-btn.recording {
  animation: pulse 2s infinite;
}

.voice-upload {
  flex: 1;
}

.voice-upload-btn {
  width: 100%;
  height: 36px;
  border-radius: 6px;
}

.voice-file-list {
  .file-item {
    border-radius: 6px;
    overflow: hidden;
    &:not(:last-child) {
      margin-bottom: 10px;
    }
    :deep(.el-loading-mask) {
      background: rgba(255, 255, 255, 0.3);
      .el-loading-spinner {
        margin-top: -15px;
        .circular {
          width: 30px;
          height: 30px;
        }
      }
    }
  }
  .file-info, .recording-info {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px;
    background: #EFEFEF;
    :deep(.el-icon:first-child) {
      cursor: pointer;
      transform: rotate(180deg);
      transform-origin: center center;
    }
    .expanded-icon {
      transform: rotate(0) !important;
    }
    .file-name {
      width: calc(100% - 60px);
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      font-weight: 400;
    }
    .file-recording {
      margin: 0 auto;
      overflow: hidden;
      animation: voice 1.5s infinite;
      @keyframes voice {
        0% { width: fit-content !important }
        50% { width: 0px }
        100% { width: fit-content !important }
      }
    }
  }
  .file-actions {
    display: flex;
    align-items: center;
    padding: 0 10px 10px;
    background: #EFEFEF;
    .play-action {
      flex: 1;
      display: flex;
      align-items: center;
      gap: 5px;
      margin-right: 8px;
      :deep(.el-icon) {
        cursor: pointer;
      }
    }
    :deep(.el-progress) {
      width: 60%;
      margin-right: 8px;
    }
  }
  .file-result {
    line-height: 20px;
    padding: 14px 14px 10px;
    border: 1px solid #DDDDDD;
    border-top: 0;
    border-bottom-left-radius: 6px;
    border-bottom-right-radius: 6px;
    box-sizing: border-box;
    animation: heightAuto .6s linear;
    textarea {
      width: 100%;
      min-height: 0;
      line-height: 20px;
      border: none;
      padding: 0;
      overflow: hidden;
      resize: none;
      outline: none;
      color: #555;
      font-family: var(--el-font-family);
    }
    @keyframes heightAuto {
      0% { max-height: 30px; }
      100% { max-height: 200px; }
    }
  }
}

.voice-backtop {
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #999;
  border-top: 1px solid #E1E1E1;
  cursor: pointer;
}
/* 调整进度条样式 */
:deep(.el-progress-bar__outer) {
  background-color: #D9D9D9;
}

:deep(.el-progress-bar__inner) {
  background-color: var(--el-color-primary);
}

:deep(.el-progress__text) {
  min-width: 14px;
  font-size: 14px !important;
  color: var(--el-color-primary);
}

.result-actions {
  margin-top: 6px;
  display: flex;
  justify-content: flex-end;
  :deep(.el-button) {
    min-width: 77px;
    height: 22px;
    line-height: 22px;
    background: none;
    border-radius: 2px;
    &:hover {
      color: var(--el-color-primary);
    }
  }
}

@keyframes pulse {
  0% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@media (max-width: 1366px) {
  .voice-btn {
    width: 50%;
  }
}
</style>
